import { getEdgeFunctionHeaders } from 'common/src/supabase';
import axios from 'axios';
import { convertExcelDate } from 'common/src/utils/functions';
import { SettlementData } from 'common/src/utils/types';
import { getSpeciesIdExcelNameMap } from 'common/src/api/species';

// Extended interface that overrides species_id type
interface ExtendedSettlementData extends Omit<SettlementData, 'species_id'> {
  species_id?: number;
}

export const settlementColumnMapping: { [key: string]: string } = {
  exportadora: 'exportadora',
  referencia: 'referencia',
  cliente: 'cliente',
  nave: 'nave',
  'mercado venta': 'mercado_venta',
  'mercado comparable': 'mercado_comparable',
  'nro contenedor': 'nro_contenedor',
  'fecha venta': 'fecha_venta',
  'tipo de negocio': 'business_type',
  'tipo negocio': 'business_type',
  'orden de venta': 'sale_order',
  'orden venta': 'sale_order',
  'pallet / identificador': 'pallet_identificador',
  'embalaje / etiqueta': 'embalaje_etiqueta',
  formato: 'formato',
  'peso bulto': 'peso_bulto',
  kilos: 'kilos',
  variedad: 'variedad',
  'calibre color ok': 'calibre_color',
  calibre: 'calibre_color',
  color: 'color',
  categoria: 'categoria',
  'bultos cargados': 'bultos_cargados',
  moneda: 'moneda',
  clausula: 'clausula',
  'ingreso moneda origen': 'ingreso_moneda_origen',
  'tax moneda origen': 'tax_moneda_origen',
  'comisión moneda origen': 'comision_moneda_origen',
  'costos logísticos + otros moneda origen': 'costos_logisticos_moneda_origen',
  'resultado cif moneda origen': 'resultado_cif_moneda_origen',
  't/c': 'tipo_cambio',
  'flete usd (mar o aire)': 'flete_usd',
  'resultado fob usd': 'resultado_fob_usd',
  especie: 'species_id', // Map ESPECIE directly to species_id
};

export class SettlementProcessor {
  private static transformColumnName(columnName: string): string {
    const cleanName = columnName
      .replace(/[\r\n]+/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[（]/g, '(')
      .replace(/[）]/g, ')')
      .trim()
      .toLowerCase();

    return settlementColumnMapping[cleanName] || cleanName;
  }

  private static transformValue(key: string, value: any): string | number {
    if (key === 'fecha_venta') {
      return convertExcelDate(value)?.split('T')[0] || new Date().toISOString();
    }

    if (
      [
        'peso_bulto',
        'ingreso_moneda_origen',
        'tax_moneda_origen',
        'comision_moneda_origen',
        'costos_logisticos_moneda_origen',
        'resultado_cif_moneda_origen',
        'tipo_cambio',
        'flete_usd',
        'resultado_fob_usd',
        'kilos',
      ].includes(key)
    ) {
      return Number(value) || 0;
    }

    // Handle bultos_cargados as integer (quantity of packages)
    if (key === 'bultos_cargados') {
      return Math.round(Number(value)) || 0;
    }

    // Ensure string values for all other fields
    return (value || '').toString();
  }

  static async transformData(
    rawData: any[]
  ): Promise<Partial<ExtendedSettlementData>[]> {
    // Obtener el mapa de excel_name a ID de especies una sola vez
    const speciesMap = await getSpeciesIdExcelNameMap();
    // console.log('Species Map:', speciesMap);

    // Valor predeterminado para species_id (CHERRY)
    const defaultSpeciesId = 2; // Asumiendo que CHERRY tiene ID 2

    return rawData
      .map((row) => {
        const transformedRow: Partial<ExtendedSettlementData> = {};
        let speciesExcelName = '';

        Object.entries(row).forEach(([key, value]) => {
          // Handle ESPECIE column specially
          if (key === 'ESPECIE' || key === 'Especie' || key === 'especie') {
            // Guardar el valor de excel_name (especie) en mayúsculas
            speciesExcelName = value ? value.toString().toUpperCase() : '';
          } else {
            const normalizedKey = this.transformColumnName(key);
            // Usar indexación segura
            (transformedRow as any)[normalizedKey] = this.transformValue(
              normalizedKey,
              value
            );
          }
        });

        // Si se encontró un excel_name, buscar el ID correspondiente
        if (speciesExcelName && speciesMap[speciesExcelName]) {
          transformedRow.species_id = speciesMap[speciesExcelName];
        } else if (speciesExcelName) {
          // Si hay un excel_name pero no se encuentra en el mapa, registrar advertencia
          console.warn(
            `No species ID found for excel_name: ${speciesExcelName}, using default ID (${defaultSpeciesId})`
          );
          transformedRow.species_id = defaultSpeciesId;
        }

        // Aseguramos que todos los campos requeridos estén presentes
        const requiredFields: (keyof SettlementData)[] = [
          'exportadora',
          'referencia',
          'cliente',
          'nave',
          'mercado_venta',
          'nro_contenedor',
          'fecha_venta',
          'pallet_identificador',
          'embalaje_etiqueta',
          'formato',
          'peso_bulto',
          'variedad',
          'calibre_color',
          'color',
          'categoria',
          'bultos_cargados',
          'kilos',
          'moneda',
          'clausula',
          'ingreso_moneda_origen',
          'tax_moneda_origen',
          'comision_moneda_origen',
          'costos_logisticos_moneda_origen',
          'resultado_cif_moneda_origen',
          'tipo_cambio',
          'flete_usd',
          'resultado_fob_usd',
          'season',
        ];

        requiredFields.forEach((field) => {
          if (!(transformedRow as any)[field]) {
            (transformedRow as any)[field] = this.transformValue(field, null);
          }
        });

        return transformedRow;
      })
      .filter((row) => row.exportadora && row.referencia && row.cliente);
  }

  static async upload(
    data: any[],
    options?: { speciesIds?: string[] }
  ): Promise<boolean | unknown> {
    try {
      // console.log(import.meta.env.VITE_SUPABASE_URL);
      const { data: result } = await axios.post(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-settlements`,
        {
          documentType: 'settlements',
          data: data,
          // No enviamos speciesIds porque ya cada registro tiene su species_id
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(await getEdgeFunctionHeaders()),
          },
        }
      );

      if (!result) {
        throw new Error('Error al procesar el documento');
      }

      return true;
    } catch (error) {
      console.error('Error uploading settlements:', error);
      throw error;
    }
  }
}
